import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from './shared/utils/api';
import { formatDate } from './shared/utils/dateUtils';

function scoreBadgeClass(pct) {
  if (pct == null) return 'ims-score-none';
  if (pct >= 70)   return 'ims-score-green';
  if (pct >= 40)   return 'ims-score-amber';
  return 'ims-score-red';
}

function Header() {
  return (
    <header className="ims-header">
      <div className="ims-header-inner">
        <a href="/app" className="ims-brand">
          <svg viewBox="0 0 64 64" width="28" height="28" aria-hidden="true">
            <polygon points="6,54 24,54 34,30 16,30" fill="#ffffff" opacity="0.3"/>
            <polygon points="20,46 38,46 48,22 30,22" fill="#ffffff" opacity="0.6"/>
            <polygon points="34,38 52,38 62,14 44,14" fill="#ffffff" opacity="0.95"/>
          </svg>
          <span className="ims-wordmark"><span className="ims-wm-sqe">SQE</span><span className="ims-wm-track">Track</span></span>
        </a>
      </div>
    </header>
  );
}

function ShimmerLoading() {
  return (
    <>
      <div className="ims-shimmer-card">
        <div className="ims-shimmer ims-shimmer-block ims-shimmer-wide" />
        <div className="ims-shimmer ims-shimmer-block ims-shimmer-med" />
        <div className="ims-shimmer-flex-row">
          {[1,2,3].map(i => (
            <div key={i} className="ims-shimmer ims-shimmer-tile" />
          ))}
        </div>
      </div>
      <div className="ims-shimmer-card">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="ims-shimmer ims-shimmer-block" style={{ width: `${80 - i * 8}%` }} />
        ))}
      </div>
    </>
  );
}

function StudyUnitSelect({ allStudyUnits, value, onChange }) {
  const bySubject = allStudyUnits.reduce((acc, su) => {
    const key = su.subject_area || su.name.split(' — ')[0] || 'Other';
    (acc[key] = acc[key] || []).push(su);
    return acc;
  }, {});

  return (
    <select
      className="ims-override-select"
      value={value || ''}
      onChange={e => onChange(e.target.value ? parseInt(e.target.value, 10) : null)}
    >
      <option value="">— Select study unit —</option>
      {Object.entries(bySubject).map(([group, units]) => (
        <optgroup key={group} label={group}>
          {units.map(su => (
            <option key={su.id} value={su.id}>{su.name}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

export default function ImportSummary({ showHeader = true }) {
  const params   = new URLSearchParams(window.location.search);
  const importId = params.get('id');
  const hasToken = !!localStorage.getItem('sqetrack_token');

  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [batch,         setBatch]         = useState(null);
  const [studyUnits,    setStudyUnits]    = useState([]);
  const [unmatched,     setUnmatched]     = useState([]);
  const [summary,       setSummary]       = useState(null);
  const [allStudyUnits, setAllStudyUnits] = useState([]);
  const [assignments,   setAssignments]   = useState({});
  const [saving,        setSaving]        = useState(false);
  const [saveMsg,       setSaveMsg]       = useState(null);
  const [savedCount,    setSavedCount]    = useState(0);
  const [reportOpen,    setReportOpen]    = useState(false);
  const [reportType,    setReportType]    = useState('');
  const [reportNotes,   setReportNotes]   = useState('');
  const [reportSaving,  setReportSaving]  = useState(false);
  const [reportDone,    setReportDone]    = useState(false);
  const [reportErr,     setReportErr]     = useState(null);
  const savingRef    = useRef(false);
  const saveMsgTimer = useRef(null);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.has('token')) {
      p.delete('token');
      const clean = p.toString() ? `?${p.toString()}` : '';
      window.history.replaceState({}, '', window.location.pathname + clean);
    }
  }, []);

  const load = useCallback(async () => {
    if (!importId) { setError('no-id'); setLoading(false); return; }
    try {
      const [importData, suData] = await Promise.all([
        api(`/api/import/${importId}`),
        api('/api/study-units'),
      ]);
      if (importData.error) {
        setError(importData.error === 'Import not found' ? 'not-found' : 'server');
        return;
      }
      setBatch(importData.batch);
      setStudyUnits(importData.study_units || []);
      setUnmatched(importData.unmatched_topics || []);
      setSummary(importData.summary || null);
      if (Array.isArray(suData)) setAllStudyUnits(suData);
    } catch {
      setError('network');
    }
    setLoading(false);
  }, [importId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (saveMsgTimer.current) clearTimeout(saveMsgTimer.current); }, []);

  const pendingCount = Object.keys(assignments).length;

  const handleSave = useCallback(async () => {
    if (!pendingCount || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSaveMsg(null);
    const count = pendingCount;
    try {
      const provider = batch?.provider === 'barbri' ? 'barbri' : 'lawdrills';
      for (const [source_label, study_unit_id] of Object.entries(assignments)) {
        if (!study_unit_id) continue;
        await api('/api/import/assign-mapping', {
          method: 'POST',
          body: JSON.stringify({ provider, source_label, study_unit_id }),
        });
      }
      setSavedCount(count);
      setSaveMsg('saved');
      setAssignments({});
      saveMsgTimer.current = setTimeout(() => setSaveMsg(null), 4000);
      await load();
    } catch (e) {
      setSaveMsg(`error:${e.message}`);
    }
    savingRef.current = false;
    setSaving(false);
  }, [assignments, batch, pendingCount, load]);

  const handleReport = useCallback(async () => {
    if (!reportType || reportSaving) return;
    setReportSaving(true);
    setReportErr(null);
    try {
      await api('/api/import/report-error', {
        method: 'POST',
        body: JSON.stringify({
          batch_id:   importId ? parseInt(importId, 10) : null,
          provider:   batch?.provider || '',
          error_type: reportType,
          notes:      reportNotes || null,
        }),
      });
      setReportDone(true);
      setReportOpen(false);
    } catch (e) {
      setReportErr('Could not submit report. Try again.');
    }
    setReportSaving(false);
  }, [reportType, reportNotes, reportSaving, importId, batch]);

  if (!hasToken) return (
    <div className={showHeader ? 'ims-page' : 'page-container'}>
      {showHeader && <Header />}
      <div className="ims-content">
        <div className="ims-state-card">
          <div className="ims-state-title">Log in to view your results</div>
          <div className="ims-state-body">Log in to SQETrack to view import results.</div>
          <a className="ims-cta-btn" href="/login">Log in to SQETrack</a>
        </div>
      </div>
    </div>
  );

  if (loading) return (
    <div className={showHeader ? 'ims-page' : 'page-container'}>
      {showHeader && <Header />}
      <div className="ims-content"><ShimmerLoading /></div>
    </div>
  );

  if (error === 'not-found') return (
    <div className={showHeader ? 'ims-page' : 'page-container'}>
      {showHeader && <Header />}
      <div className="ims-content">
        <div className="ims-state-card">
          <div className="ims-state-title">Import not found</div>
          <div className="ims-state-body">This import may have been replaced by a newer one.</div>
          <a className="ims-cta-btn" href="/app">Go to dashboard</a>
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div className={showHeader ? 'ims-page' : 'page-container'}>
      {showHeader && <Header />}
      <div className="ims-content">
        <div className="ims-state-card">
          <div className="ims-state-title">Could not load this import</div>
          <div className="ims-state-body">{
            error === 'no-id' ? 'No import ID was provided in the URL.' :
            'Check your connection and try again.'
          }</div>
          <button className="ims-cta-btn" onClick={() => { setError(null); setLoading(true); load(); }}>
            Try again
          </button>
        </div>
      </div>
    </div>
  );

  const providerLabel = batch?.provider === 'barbri' ? 'BARBRI' : 'Law Drills';
  const providerName  = batch?.provider === 'barbri' ? 'BARBRI' : 'Law Drills';
  const paperLabel    = batch?.paper ? ` · ${batch.paper}` : '';
  const isEmpty       = summary && !summary.matched && !summary.total_questions;

  // Detect multi-mapped source labels: same label appears on more than one study unit row
  const labelCounts = {};
  studyUnits.forEach(su => {
    (su.source_labels || []).forEach(label => {
      labelCounts[label] = (labelCounts[label] || 0) + 1;
    });
  });
  const hasMultiMaps = Object.values(labelCounts).some(c => c > 1);

  const emptyReportMailto = () => {
    const body = `Batch ID: ${importId}\nProvider: ${providerLabel}\n\n`;
    return `mailto:toby@everythingseeds.co.uk?subject=${encodeURIComponent(`Import problem — ${providerLabel} batch ${importId}`)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className={showHeader ? 'ims-page' : 'page-container'}>
      {showHeader && <Header />}
      <div className="ims-content">

        <a className="ims-back" href="/app">← Back to dashboard</a>

        <div className="ims-summary-card">
          <div className="ims-title">
            <svg className="ims-title-check" width="28" height="28" viewBox="0 0 28 28" fill="none">
              <circle cx="14" cy="14" r="14" className="ims-check-bg"/>
              <path d="M8 14l4.5 4.5L20 8" className="ims-check-stroke" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Import complete
          </div>

          {summary && (
            <div className="ims-stats">
              <div className="ims-stat-box">
                <div className="ims-stat-num">{summary.matched}</div>
                <div className="ims-stat-label">study units updated</div>
              </div>
              <div className="ims-stat-box">
                <div className="ims-stat-num">{summary.total_questions}</div>
                <div className="ims-stat-label">questions imported</div>
              </div>
              {summary.unmatched > 0 && (
                <div className="ims-stat-box">
                  <div className="ims-stat-num">{summary.unmatched}</div>
                  <div className="ims-stat-label">unmatched topics</div>
                </div>
              )}
            </div>
          )}

          {summary && summary.matched > 0 && (
            <div className="ims-confidence-line">
              Confidence scores updated for {summary.matched} study {summary.matched === 1 ? 'unit' : 'units'}
            </div>
          )}

          <div className="ims-meta-line">
            {providerLabel}{paperLabel} · {formatDate(batch?.imported_at)}
          </div>

          {isEmpty ? (
            <div className="ims-empty-import">
              <div className="ims-empty-import-title">No topics could be imported</div>
              <div className="ims-empty-import-body">
                This usually means the performance data was not loaded before importing. Visit your {providerName} performance page, wait for scores to load, then import again.
              </div>
              <a className="ims-report-link" href={emptyReportMailto()}>
                Report a problem
              </a>
            </div>
          ) : (
            <a className="ims-cta-btn" href="/app">View dashboard</a>
          )}

          {unmatched.length > 0 && (
            <div className="ims-unmatched-notice">
              {unmatched.length} topic{unmatched.length !== 1 ? 's' : ''} could not be matched. Review them below.
            </div>
          )}
        </div>

        {!isEmpty && (
          <div className="ims-detail-card">
            <div className="ims-detail-heading">Study units updated</div>

            {studyUnits.length > 0 && (
              <>
                {hasMultiMaps && (
                  <div className="ims-multimap-note">
                    Some {providerName} topics cover multiple study units. Scores are applied equally to each.
                  </div>
                )}
                <table className="ims-table">
                  <thead>
                    <tr>
                      <th className="ims-td-ld">Study unit</th>
                      <th className="ims-td-score">Score</th>
                      <th className="ims-td-sqe">{providerName} topics</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studyUnits.map(su => {
                      const pct = su.score_pct != null ? Math.round(su.score_pct) : null;
                      return (
                        <tr key={su.study_unit_id}>
                          <td className="ims-td-ld">{su.name}</td>
                          <td className="ims-td-score">
                            <span className={`ims-score-badge ${scoreBadgeClass(pct)}`}>
                              {pct != null ? `${pct}%` : '—'}
                            </span>
                          </td>
                          <td className="ims-td-sqe">
                            <div className="ims-sqe-sub">
                              {su.source_labels.map((label, i) => (
                                <span key={label}>
                                  {label}
                                  {labelCounts[label] > 1 && (
                                    <span className="ims-multimap-badge"> (shared)</span>
                                  )}
                                  {i < su.source_labels.length - 1 && ' · '}
                                </span>
                              ))}
                            </div>
                            <div className="ims-sqe-sub">{su.attempted}q</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="ims-report-row">
                  {reportDone ? (
                    <span className="ims-report-done">Report submitted. We'll review this and update the mapping if needed.</span>
                  ) : reportOpen ? (
                    <div className="ims-report-form">
                      <select
                        className="ims-report-select"
                        value={reportType}
                        onChange={e => setReportType(e.target.value)}
                      >
                        <option value="">What's wrong?</option>
                        <option value="wrong_study_unit">Wrong study unit assigned</option>
                        <option value="wrong_subject_area">Topic should map to a different area</option>
                        <option value="topic_missing">Topic is missing from SQETrack</option>
                        <option value="other">Other</option>
                      </select>
                      <textarea
                        className="ims-report-notes"
                        placeholder="Additional notes (optional)"
                        value={reportNotes}
                        onChange={e => setReportNotes(e.target.value)}
                        rows={2}
                      />
                      {reportErr && <span className="ims-report-err">{reportErr}</span>}
                      <div className="ims-report-actions">
                        <button
                          className="ims-report-submit"
                          disabled={!reportType || reportSaving}
                          onClick={handleReport}
                        >
                          {reportSaving ? 'Submitting…' : 'Submit report'}
                        </button>
                        <button className="ims-report-cancel" onClick={() => { setReportOpen(false); setReportErr(null); }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button className="ims-report-link-btn" onClick={() => setReportOpen(true)}>
                      Report mapping error
                    </button>
                  )}
                </div>
              </>
            )}

            {unmatched.length > 0 && (
              <>
                <div className="ims-sub-heading">{unmatched.length} unmatched topic{unmatched.length !== 1 ? 's' : ''}</div>
                <div className="ims-sub-hint">These topics have no study unit mapping. Assign them below to include their scores in your confidence.</div>
                <table className="ims-table">
                  <thead>
                    <tr>
                      <th className="ims-td-ld">Topic label</th>
                      <th className="ims-td-sqe">Assign to study unit</th>
                      <th className="ims-td-status">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unmatched.map(label => {
                      const assigned = assignments.hasOwnProperty(label);
                      return (
                        <tr key={label}>
                          <td className="ims-td-ld">{label}</td>
                          <td className="ims-td-sqe">
                            <StudyUnitSelect
                              allStudyUnits={allStudyUnits}
                              value={assignments[label] || null}
                              onChange={suId => {
                                if (!suId) {
                                  setAssignments(prev => { const n = {...prev}; delete n[label]; return n; });
                                } else {
                                  setAssignments(prev => ({ ...prev, [label]: suId }));
                                }
                              }}
                            />
                          </td>
                          <td className="ims-td-status">
                            {assigned
                              ? <span className="ims-status-assigned">Assigned</span>
                              : <span className="ims-status-unmatched">Unmatched</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="ims-actions">
                  {saveMsg === 'saved' && (
                    <span className="ims-save-confirm">
                      Mappings saved. Confidence scores updated for {savedCount} study {savedCount === 1 ? 'unit' : 'units'}.
                    </span>
                  )}
                  {saveMsg && saveMsg.startsWith('error:') && (
                    <span className="ims-status-edited">{saveMsg.slice(6)}</span>
                  )}
                  <button
                    className="ims-save-btn"
                    disabled={!pendingCount || saving}
                    onClick={handleSave}
                  >
                    {saving ? 'Saving' : `Save mappings${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
                  </button>
                </div>
              </>
            )}

          </div>
        )}

      </div>
    </div>
  );
}
